import type { AnalyticsProps } from '../../types/assertive'
import loadScriptAsync from '../loadScriptAsync'
import debug from '../console'
const logger = debug.withTag('utils:scriptLoadAY')

export async function scriptLoadAY(
  ayEntityId: string | unknown | null,
  analyticsProps: AnalyticsProps,
  layoutName: string,
  layoutSkeleton: string,
  cohort: string | null,
  articleId: string | null,
  userId: string | null,
  isDevMode: boolean,
  devDisableAds: boolean,
  devAYTestEntityId: string | null,
  pageview_id: string | null
): Promise<boolean> {
  if (isDevMode && devDisableAds) {
    logger.info('Ads are disabled in development mode')
    return false
  }

  if (window.assertive && window.ayManagerEnv) {
    return true
  }

  const entityId =
    isDevMode && devAYTestEntityId ? devAYTestEntityId : ayEntityId

  // Ensure entityId is a string
  if (!entityId || typeof entityId !== 'string') {
    logger.warn(
      'Entity ID is not a valid string, cannot initialize Assertive Yield'
    )
    return false
  }

  const analyticsOptions = {
    integrations: { webAnalytics: true },
    logUnfilled: true,

    // When adding new custom dimensions please update the docs here
    // https://redbrickmedia.atlassian.net/wiki/spaces/Duplex/pages/3727458322/Assertive+Yield+Analytic+Mappings
    custom: {
      userState: null,
      layout: layoutName,
      custom_1: analyticsProps.type_tag,
      custom_2: analyticsProps.ntp_app_version,
      custom_3: analyticsProps.host_browser_app_version,
      custom_4: getTruncatedUrl(),
      custom_5: layoutSkeleton,
      custom_6: cohort,
      custom_7: articleId,
      custom_8: userId, // composite `<userId>-<installDate>`
      custom_9: pageview_id,
      custom_10: null, // lastEngagementAction — set dynamically via updateUserEngagementTracking
      custom_11: null, // daysSinceLastEngagement — set dynamically via updateUserEngagementTracking
      custom_12: null, // daysSinceInstall — set dynamically via updateUserEngagementTracking
    },
    useHistoryChangeTrigger: true,
  }
  const assertiveOptions = {
    entityId: entityId,
    debug: false,
    analytics: analyticsOptions,
  }

  window.assertiveYieldAnalytics ??= { ...analyticsOptions }
  window.assertive ??= { ...assertiveOptions }

  try {
    if (entityId) {
      await loadScriptAsync('head', {
        src: `https://${entityId}.ay.delivery/manager/${entityId}`,
      })
    }
  } catch (error) {
    logger.error('Failed to load Assertive Yield SDK:', error)
    return false
  }

  // since we need flags to resolve we push to the queue to ensure it runs after the sdk is loaded and flags are resolved
  window.assertiveQueue = window.assertiveQueue || []
  window.assertiveQueue.push(function () {
    if (window.assertive?.analytics?.custom) {
      window.assertive.analytics.custom.custom_5 = layoutSkeleton
      window.assertive.analytics.custom.custom_6 = cohort
      window.assertive.analytics.custom.custom_7 = articleId
      window.assertive.analytics.custom.custom_8 = userId
      window.assertive.analytics.custom.custom_9 = pageview_id
    }
  })

  return true
}
